import Repository from '../../repositories/RepositoryFactory';
const SchedulesRepository = Repository.get('schedules');

// Helper: generate colors for occurrences. Pulled out of the actions so it can be
// used directly without relying on `this` (Vuex actions don't have `this.generateOccurrenceColors`).
const generateOccurrenceColors = (schedules) => {
	const toHex = (h, s, l) => {
		s /= 100;
		l /= 100;
		const k = (n) => (n + h / 30) % 12;
		const a = s * Math.min(l, 1 - l);
		const f = (n) =>
			Math.round((l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))) * 255)
				.toString(16)
				.padStart(2, '0');
		return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
	};

	const getRelativeLuminance = (r, g, b) => {
		const rs = r / 255;
		const gs = g / 255;
		const bs = b / 255;

		const rLinear = rs <= 0.03928 ? rs / 12.92 : Math.pow((rs + 0.055) / 1.055, 2.4);
		const gLinear = gs <= 0.03928 ? gs / 12.92 : Math.pow((gs + 0.055) / 1.055, 2.4);
		const bLinear = bs <= 0.03928 ? bs / 12.92 : Math.pow((bs + 0.055) / 1.055, 2.4);

		return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
	};

	const getContrastRatio = (color1Luminance, color2Luminance) => {
		const lighter = Math.max(color1Luminance, color2Luminance);
		const darker = Math.min(color1Luminance, color2Luminance);
		return (lighter + 0.05) / (darker + 0.05);
	};

	const hexToRgb = (hex) => {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
		return result
			? {
					r: parseInt(result[1], 16),
					g: parseInt(result[2], 16),
					b: parseInt(result[3], 16),
			  }
			: null;
	};

	const getBestTextColor = (backgroundColor) => {
		const rgb = hexToRgb(backgroundColor);
		if (!rgb) return '#FFFFFF';

		const bgLuminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
		const whiteLuminance = 1;
		const blackLuminance = 0;

		const whiteContrast = getContrastRatio(bgLuminance, whiteLuminance);
		const blackContrast = getContrastRatio(bgLuminance, blackLuminance);

		return whiteContrast > blackContrast ? '#fff' : '#000';
	};

	const occurrenceColors = {};
	const goldenAngle = 137.508;
	schedules.forEach((schedule) => {
		(schedule.scheduleOccurrences || []).forEach((occ) => {
			const hueStep = occ.id * goldenAngle;
			const h = hueStep % 360;
			const s = 50 + (hueStep % 40);
			const l = 45 + (hueStep % 30);
			const color = toHex(h, s, l);
			const textColor = getBestTextColor(color);
			occurrenceColors[occ.id] = { color, textColor };
		});
	});

	return occurrenceColors;
};

export default {
	namespaced: true,
	state: {
		schedulesByOwnerId: [],
		schedulesByLocationId: [],
	},
	mutations: {
		GET_SCHEDULES_BY_OWNER_ID (state, payload) {
			state.schedulesByOwnerId = payload;
		},
		GET_SCHEDULES_BY_LOCATION_ID (state, payload) {
			state.schedulesByLocationId = payload;
		},
		ADD_SCHEDULE (state, payload) {
			state.schedulesByLocationId.push(payload);
		},
	},
	actions: {
		async getSchedulesByOwnerId ({ commit }, ownerId) {
			const response = await SchedulesRepository.getSchedulesByOwnerId(ownerId);
			commit('GET_SCHEDULES_BY_OWNER_ID', response);
			return response;
		},
		async getSchedulesByLocationId ({ commit }, locationId) {
			let response = await SchedulesRepository.getSchedulesByLocationId(locationId);
			const colors = generateOccurrenceColors(response);
			response = response.map((schedule) => {
				const scheduleOccurrences = (schedule.scheduleOccurrences || []).map((occ) => ({
					...occ,
					color: colors[occ.id]?.color || '#CCCCCC',
					textColor: colors[occ.id]?.textColor || '#000000',
				}));
				return {
					...schedule,
					scheduleOccurrences,
				};
			});
			commit('GET_SCHEDULES_BY_LOCATION_ID', response);
			return response;
		},
		async deleteScheduleById ({ commit }, id) {
			const response = await SchedulesRepository.deleteScheduleById(id);
			return response;
		},

		async createSchedule ({ commit }, body) {
			const response = await SchedulesRepository.createSchedule(body);
			return response;
		},

		async createScheduleTaskDetail ({ commit }, body) {
			const response = await SchedulesRepository.createScheduleTaskDetail(body);
			return response;
		},
		async updateSchedule ({ commit }, body) {
			const response = await SchedulesRepository.updateSchedule(body);
			return response;
		},
		async editScheduleTaskDetail ({ commit }, body) {
			const response = await SchedulesRepository.editScheduleTaskDetail(body);
			return response;
		},
		async deleteScheduleTaskDetail ({ commit }, id) {
			const response = await SchedulesRepository.deleteScheduleTaskDetail(id);
			return response;
		},
	},
};
